"use client";

import {
  CLICK_ID_COOKIE_NAME,
  CLICK_ID_KEYS,
  UTM_KEYS,
  FIRST_TOUCH_COOKIE_NAME,
  LAST_TOUCH_COOKIE_NAME,
  isInternalDomain,
  parseClickIdCookie,
  parseLandingCookie,
} from "@/utils/click-id-cookie";
import { getCalloutCampaign } from "@/lib/marketing/community-callout";
import { getProofCampaign } from "@/lib/marketing/aspirational-proof";
import { getSquareCampaign } from "@/lib/marketing/prizepicks-square";
import { CREATOR_BIO_FLAG } from "@/lib/marketing/creator-bio";

export { CLICK_ID_KEYS, UTM_KEYS };

export type AttributionProps = Record<
  string,
  string | number | null | undefined
>;

const FIRST_TOUCH_KEY = "upsider_attribution_first";
const LAST_TOUCH_KEY = "upsider_attribution_last";

const ATTR_COOKIE_NAME = "upsider_attr";
const ATTR_COOKIE_MAX_AGE = 60 * 60 * 24 * 180;

export type Channel =
  | "paid_social"
  | "organic_social"
  | "paid_search"
  | "organic_search"
  | "referral"
  | "affiliate"
  | "direct"
  | "email"
  | "unknown";

const SOCIAL_BASE_DOMAINS = [
  "twitter.com",
  "x.com",
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "youtube.com",
  "tiktok.com",
  "reddit.com",
  "pinterest.com",
];

const SOCIAL_EXACT_DOMAINS = new Set([
  "t.co",
  "com.twitter.android",
  "fb.com",
  "youtu.be",
]);


const SEARCH_DOMAINS = new Set([
  "google.com",
  "google.co.uk",
  "bing.com",
  "yahoo.com",
  "duckduckgo.com",
  "baidu.com",
  "yandex.com",
]);

const PAID_MEDIUMS = new Set([
  "cpc",
  "ppc",
  "paid",
  "paidsocial",
  "paid_social",
  "paid-social",
  "cpm",
  "display",
  "banner",
  "retargeting",
  "remarketing",
]);

const KNOWN_X_ADS = [
  {
    campaign: "x_creative_proof_20260805",
    campaignId: "41992869",
    adGroupId: "56056040",
    postIds: {
      "w1a-morelosses-plusev-14718": "2085056625237209429",
      "w1b-431231-cinematic-5594": "2085052518594400332",
      "w2b-same10-earlyjuice-946": "2085050995541770697",
    },
  },
  {
    campaign: "x_paid",
    campaignId: "41992869",
    adGroupId: "55924402",
    postIds: {
      product_demo_ev_sniper: "2076738851251630408",
    },
  },
] as const;

function stripXIdPlaceholder(value: string, id: string): string | null {
  const suffixes = [`__{{${id}}}`, `__%7B%7B${id}%7D%7D`];
  const suffix = suffixes.find((candidate) =>
    value.toLowerCase().endsWith(candidate.toLowerCase())
  );
  return suffix ? value.slice(0, -suffix.length) : null;
}

function isXIdPlaceholder(value: unknown, id: string): boolean {
  if (typeof value !== "string") return false;
  const normalized = value.toLowerCase();
  return (
    normalized === `{{${id}}}`.toLowerCase() ||
    normalized === `%7B%7B${id}%7D%7D`.toLowerCase()
  );
}

function repairKnownXAdIds(
  attribution: AttributionProps,
  keyPrefix: "" | "initial_" = ""
): boolean {
  const sourceKey = `${keyPrefix}utm_source`;
  const mediumKey = `${keyPrefix}utm_medium`;
  const campaignKey = `${keyPrefix}utm_campaign`;
  const termKey = `${keyPrefix}utm_term`;
  const contentKey = `${keyPrefix}utm_content`;

  const source = String(attribution[sourceKey] || "").toLowerCase();
  const medium = String(attribution[mediumKey] || "").toLowerCase();
  if ((source !== "twitter" && source !== "x") || medium !== "cpc") {
    return false;
  }
  if (!isXIdPlaceholder(attribution[termKey], "adgroup_id")) return false;
  if (typeof attribution[campaignKey] !== "string") return false;
  if (typeof attribution[contentKey] !== "string") return false;

  const campaign = stripXIdPlaceholder(
    attribution[campaignKey],
    "campaign_id"
  );
  const creative = stripXIdPlaceholder(attribution[contentKey], "ad_id");
  if (!campaign || !creative) return false;

  const knownAd = KNOWN_X_ADS.find((entry) => entry.campaign === campaign);
  if (!knownAd) return false;

  const postId = Object.entries(knownAd.postIds).find(
    ([creativeName]) => creativeName === creative
  )?.[1];
  if (!postId) return false;

  attribution[campaignKey] = `${campaign}__${knownAd.campaignId}`;
  attribution[termKey] = knownAd.adGroupId;
  attribution[contentKey] = `${creative}__${postId}`;
  return true;
}

export const REFERRAL_KEYS = ["via"] as const;

export const MEANINGFUL_ATTRIBUTION_KEYS = [
  ...UTM_KEYS,
  ...CLICK_ID_KEYS,
  ...REFERRAL_KEYS,
  "referrer",
  "referrer_domain",
] as const;

const INITIAL_KEY_MAP: Record<string, string> = {
  utm_source: "initial_utm_source",
  utm_medium: "initial_utm_medium",
  utm_campaign: "initial_utm_campaign",
  utm_term: "initial_utm_term",
  utm_content: "initial_utm_content",
  referrer: "initial_referrer",
  referrer_domain: "initial_referrer_domain",
  gclid: "initial_gclid",
  gbraid: "initial_gbraid",
  wbraid: "initial_wbraid",
  fbclid: "initial_fbclid",
  fbp: "initial_fbp",
  fbc: "initial_fbc",
  msclkid: "initial_msclkid",
  twclid: "initial_twclid",
  ttclid: "initial_ttclid",
  li_fat_id: "initial_li_fat_id",
  rdt_cid: "initial_rdt_cid",
  via: "initial_via",
  channel: "initial_channel",
};

function readCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  try {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [cookieName, ...valueParts] = cookie.trim().split("=");
      if (cookieName === name) {
        return decodeURIComponent(valueParts.join("="));
      }
    }
  } catch {
  }
  return null;
}

function formatFbcFromFbclid(fbclid: string, creationTimeMs: number, subdomainIndex = 1): string {
  return `fb.${subdomainIndex}.${creationTimeMs}.${fbclid}`;
}

function getMetaCookieIds(fbclid?: string | null, clickTimeMs = Date.now()): { fbp?: string; fbc?: string } {
  const fbp = readCookieValue("_fbp") || undefined;
  const fbcCookie = readCookieValue("_fbc") || undefined;
  if (fbcCookie) {
    return { fbp, fbc: fbcCookie };
  }
  if (fbclid) {
    return {
      fbp,
      fbc: formatFbcFromFbclid(fbclid, clickTimeMs, 1),
    };
  }
  return { fbp };
}

function isSocialDomain(domain: string): boolean {
  const cleanDomain = domain.replace(/^www\./, "").toLowerCase();
  
  if (SOCIAL_EXACT_DOMAINS.has(cleanDomain)) return true;
  
  for (const baseDomain of SOCIAL_BASE_DOMAINS) {
    if (cleanDomain === baseDomain || cleanDomain.endsWith(`.${baseDomain}`)) {
      return true;
    }
  }
  return false;
}

function isSearchDomain(domain: string): boolean {
  const cleanDomain = domain.replace(/^www\./, "").toLowerCase();
  
  for (const searchDomain of SEARCH_DOMAINS) {
    if (cleanDomain === searchDomain || cleanDomain.endsWith(`.${searchDomain}`)) {
      return true;
    }
  }
  if (/^google\.[a-z]{2,}$/.test(cleanDomain)) {
    return true;
  }
  return false;
}


export function deriveChannel(props: AttributionProps): Channel {
  const source = String(props.utm_source || "").toLowerCase();
  const medium = String(props.utm_medium || "").toLowerCase();
  const referrerDomain = String(props.referrer_domain || "").toLowerCase();

  if (props.gclid || props.gbraid || props.wbraid || props.msclkid) {
    return "paid_search";
  }
  if (props.fbclid || props.twclid || props.ttclid || props.li_fat_id || props.rdt_cid) {
    return "paid_social";
  }

  if (PAID_MEDIUMS.has(medium)) {
    const socialSources = ["twitter", "x", "facebook", "fb", "instagram", "linkedin", "tiktok", "reddit", "pinterest", "youtube"];
    if (socialSources.some((s) => source.includes(s))) {
      return "paid_social";
    }
    if (source.includes("google") || source.includes("bing") || source.includes("yahoo")) {
      return "paid_search";
    }
    return "paid_social";
  }

  if (props.via) {
    return "affiliate";
  }

  if (medium === "email") {
    return "email";
  }

  if (medium === "organic" || medium === "organic_social" || medium === "social") {
    const searchSources = ["google", "bing", "yahoo", "duckduckgo", "baidu", "yandex"];
    if (searchSources.some((s) => source.includes(s))) {
      return "organic_search";
    }
    if (referrerDomain && isSearchDomain(referrerDomain)) {
      return "organic_search";
    }
    const socialSources = ["twitter", "x", "facebook", "fb", "instagram", "linkedin", "tiktok", "reddit", "pinterest", "youtube"];
    if (socialSources.some((s) => source.includes(s))) {
      return "organic_social";
    }
    if (referrerDomain && isSocialDomain(referrerDomain)) {
      return "organic_social";
    }
    return "organic_social";
  }
  if (medium === "referral") {
    return "referral";
  }
  if (medium === "affiliate") {
    return "affiliate";
  }

  if (referrerDomain) {
    if (isInternalDomain(referrerDomain)) {
      return "direct";
    }

    if (isSocialDomain(referrerDomain)) {
      return "organic_social";
    }

    if (isSearchDomain(referrerDomain)) {
      return "organic_search";
    }

    return "referral";
  }

  return "direct";
}

function writeAttrCookie(payload: Record<string, string | number>): void {
  if (typeof document === "undefined") return;
  try {
    const compact = {
      sv: 1,
      fc: payload.first_channel || payload.channel,
      lc: payload.last_channel || payload.channel,
      fs: payload.initial_utm_source || payload.utm_source,
      ls: payload.utm_source,
      fm: payload.initial_utm_medium || payload.utm_medium,
      lm: payload.utm_medium,
      fk: payload.initial_utm_campaign || payload.utm_campaign,
      lk: payload.utm_campaign,
      v: payload.via || payload.initial_via,
      gc: payload.gclid || payload.initial_gclid,
      gb: payload.gbraid || payload.initial_gbraid,
      wb: payload.wbraid || payload.initial_wbraid,
      tw: payload.twclid || payload.initial_twclid,
      tt: payload.ttclid || payload.initial_ttclid,
      fb: payload.fbclid || payload.initial_fbclid,
      rd: payload.rdt_cid || payload.initial_rdt_cid,
      fbp: payload.fbp || payload.initial_fbp,
      fbc: payload.fbc || payload.initial_fbc,
      li: payload.li_fat_id || payload.initial_li_fat_id,
      ft: payload.first_touch_ts,
      lt: payload.last_touch_ts,
    };
    const cleaned: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(compact)) {
      if (v !== undefined && v !== null && v !== "") {
        cleaned[k] = v;
      }
    }
    let json = JSON.stringify(cleaned);
    
    if (encodeURIComponent(json).length > 3800) {
      console.warn("[Attribution] Cookie payload too large, falling back to click IDs only");
      const minimalClickIds: Record<string, string | number> = { sv: 1 };
      if (cleaned.tw) minimalClickIds.tw = cleaned.tw;
      if (cleaned.gc) minimalClickIds.gc = cleaned.gc;
      if (cleaned.gb) minimalClickIds.gb = cleaned.gb;
      if (cleaned.wb) minimalClickIds.wb = cleaned.wb;
      if (cleaned.fb) minimalClickIds.fb = cleaned.fb;
      if (cleaned.rd) minimalClickIds.rd = cleaned.rd;
      if (cleaned.fbp) minimalClickIds.fbp = cleaned.fbp;
      if (cleaned.fbc) minimalClickIds.fbc = cleaned.fbc;
      if (cleaned.tt) minimalClickIds.tt = cleaned.tt;
      if (cleaned.li) minimalClickIds.li = cleaned.li;
      if (cleaned.ft) minimalClickIds.ft = cleaned.ft;
      if (cleaned.lt) minimalClickIds.lt = cleaned.lt;
      json = JSON.stringify(minimalClickIds);
      
      if (encodeURIComponent(json).length > 3800) {
        console.warn("[Attribution] Even minimal click IDs too large, skipping cookie");
        return;
      }
    }
    const isSecure = typeof window !== "undefined" && window.location.protocol === "https:";
    const secureFlag = isSecure ? "; Secure" : "";
    document.cookie = `${ATTR_COOKIE_NAME}=${encodeURIComponent(json)}; path=/; max-age=${ATTR_COOKIE_MAX_AGE}; SameSite=Lax${secureFlag}`;
  } catch (e) {
  }
}

function safeParseStorage(key: string): AttributionProps {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as AttributionProps;
  } catch {
  }
  return {};
}

function safeWriteStorage(key: string, value: AttributionProps): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
  }
}

function repairInternalReferrer(attribution: AttributionProps, keyPrefix: "" | "initial_"): boolean {
  const domain = attribution[`${keyPrefix}referrer_domain`];
  if (typeof domain !== "string" || !isInternalDomain(domain)) return false;
  delete attribution[`${keyPrefix}referrer`];
  delete attribution[`${keyPrefix}referrer_domain`];
  if (keyPrefix) {
    const channel = deriveChannel(
      Object.fromEntries(Object.entries(INITIAL_KEY_MAP).map(([key, initialKey]) => [key, attribution[initialKey]])),
    );
    attribution.initial_channel = channel;
    attribution.first_channel = channel;
  } else {
    const channel = deriveChannel(attribution);
    attribution.channel = channel;
    attribution.last_channel = channel;
  }
  return true;
}

function readAndRepairStoredAttribution(
  key: string,
  keyPrefix: "" | "initial_" = ""
): AttributionProps {
  const attribution = safeParseStorage(key);
  const repairedAdIds = repairKnownXAdIds(attribution, keyPrefix);
  const repairedReferrer = repairInternalReferrer(attribution, keyPrefix);
  if (repairedAdIds || repairedReferrer) {
    safeWriteStorage(key, attribution);
  }
  return attribution;
}

function stripEmpty(props: AttributionProps): AttributionProps {
  const cleaned: AttributionProps = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    cleaned[k] = v;
  }
  return cleaned;
}

function hasMeaningfulAttribution(delta: AttributionProps): boolean {
  return MEANINGFUL_ATTRIBUTION_KEYS.some((key) => {
    const val = delta[key];
    return typeof val === "string" && val.trim() !== "";
  });
}

function attributionFrom(search: string, referrer: string): AttributionProps {
  const delta: AttributionProps = {};
  const params = new URLSearchParams(search);

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) delta[key] = value;
  }

  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key);
    if (value) delta[key] = value;
  }

  for (const key of REFERRAL_KEYS) {
    const value = params.get(key);
    if (value) delta[key] = value;
  }

  repairKnownXAdIds(delta);

  if (delta.via && !delta.utm_source) {
    delta.utm_source = "x";
    delta.utm_medium = "organic";
    delta.utm_campaign = String(delta.via);
  }

  if (referrer) {
    try {
      const referrerHost = new URL(referrer).hostname;
      if (!isInternalDomain(referrerHost)) {
        delta.referrer = referrer;
        delta.referrer_domain = referrerHost;
      }
    } catch {
    }
  }

  return stripEmpty(delta);
}

export function extractAttributionFromLocation(): AttributionProps {
  if (typeof window === "undefined") return {};
  let referrer = "";
  try {
    referrer = document.referrer;
  } catch {
  }
  return attributionFrom(window.location.search, referrer);
}

function landingFromCookie(name: string): { delta: AttributionProps; url: string; ts: number } | null {
  const landing = parseLandingCookie(readCookieValue(name));
  if (!landing) return null;
  const queryStart = landing.path.indexOf("?");
  const delta = attributionFrom(queryStart >= 0 ? landing.path.slice(queryStart) : "", landing.referrer ?? "");
  const metaIds = getMetaCookieIds(typeof delta.fbclid === "string" ? delta.fbclid : undefined, landing.ts);
  return {
    delta: stripEmpty({ ...delta, fbp: metaIds.fbp, fbc: metaIds.fbc }),
    url: `${window.location.origin}${landing.path}`,
    ts: landing.ts,
  };
}

let appliedReferrer: unknown;

export function persistAttribution(delta: AttributionProps): void {
  if (typeof window === "undefined") return;
  const cleanedDelta = stripEmpty(delta);
  repairKnownXAdIds(cleanedDelta);
  const referrerOnly = Object.keys(cleanedDelta).every((key) => key === "referrer" || key === "referrer_domain");
  const meaningfulDelta = hasMeaningfulAttribution(cleanedDelta);

  const metaIds = getMetaCookieIds(
    typeof cleanedDelta.fbclid === "string" ? cleanedDelta.fbclid : undefined
  );
  if (metaIds.fbp && !cleanedDelta.fbp) cleanedDelta.fbp = metaIds.fbp;
  if (metaIds.fbc && !cleanedDelta.fbc) cleanedDelta.fbc = metaIds.fbc;

  const now = Date.now();
  const currentUrl = window.location.href;

  const channel = deriveChannel(cleanedDelta);
  cleanedDelta.channel = channel;

  const first = readAndRepairStoredAttribution(FIRST_TOUCH_KEY, "initial_");
  let last = readAndRepairStoredAttribution(LAST_TOUCH_KEY);

  const hasFirstTouch = typeof first.first_touch_ts === "number";

  if (typeof last.last_touch_ts !== "number") {
    const latest = landingFromCookie(LAST_TOUCH_COOKIE_NAME);
    if (latest) {
      const latestChannel = deriveChannel(latest.delta);
      last = {
        ...latest.delta,
        channel: latestChannel,
        last_touch_url: latest.url,
        last_touch_ts: latest.ts,
        last_channel: latestChannel,
      };
      safeWriteStorage(LAST_TOUCH_KEY, last);
    }
  }

  if (!hasFirstTouch) {
    const landing = landingFromCookie(FIRST_TOUCH_COOKIE_NAME);
    const firstDelta = landing ? { ...landing.delta, channel: deriveChannel(landing.delta) } : cleanedDelta;
    const initial: AttributionProps = {};
    for (const [k, v] of Object.entries(firstDelta)) {
      const mappedKey = INITIAL_KEY_MAP[k];
      if (mappedKey) initial[mappedKey] = v;
    }
    initial.first_touch_url = landing?.url ?? currentUrl;
    initial.first_touch_ts = landing?.ts ?? now;
    initial.first_channel = firstDelta.channel ?? channel;
    const updatedFirst = { ...first, ...initial };
    safeWriteStorage(FIRST_TOUCH_KEY, updatedFirst);
  }

  const repeatReferrer = referrerOnly && cleanedDelta.referrer === appliedReferrer;
  if (typeof cleanedDelta.referrer === "string") appliedReferrer = cleanedDelta.referrer;
  const hasMeaningful = meaningfulDelta && !repeatReferrer;

  if (hasMeaningful) {
    const nextLast: AttributionProps = {
      ...last,
      ...cleanedDelta,
      last_touch_url: currentUrl,
      last_touch_ts: now,
      last_channel: channel,
    };
    const search = new URLSearchParams(window.location.search);
    if (String(last.utm_term ?? "").startsWith("proof_preview_v1_") && !cleanedDelta.utm_term) delete nextLast.utm_term;
    const callout = getCalloutCampaign(search) ?? getProofCampaign(search) ?? getSquareCampaign(search);
    if ((callout && !callout.postId) || cleanedDelta.utm_campaign === CREATOR_BIO_FLAG) delete nextLast.utm_content;
    safeWriteStorage(LAST_TOUCH_KEY, nextLast);
  } else if (!last.last_channel) {
    const nextLast: AttributionProps = {
      ...last,
      last_channel: channel,
      last_touch_ts: now,
    };
    safeWriteStorage(LAST_TOUCH_KEY, nextLast);
  }

  const allAttribution = getAttributionForEvent();
  writeAttrCookie(allAttribution as Record<string, string | number>);
}

export function getAttributionForEvent(): AttributionProps {
  const first = readAndRepairStoredAttribution(FIRST_TOUCH_KEY, "initial_");
  const last = readAndRepairStoredAttribution(LAST_TOUCH_KEY);
  const merged = stripEmpty({ ...first, ...last });
  const serverClickIds = parseClickIdCookie(readCookieValue(CLICK_ID_COOKIE_NAME));
  for (const [key, value] of Object.entries(serverClickIds)) {
    if (!merged[key] && !merged[`initial_${key}`]) merged[key] = value;
  }
  return merged;
}
